import { v } from 'convex/values'

// Recurrence schedules. Day-of-week numbers match JS getDay(): 0 = Sun … 6 = Sat.

export const RECURS_PERIODS = [
  'daily',
  'everyOtherDay',
  'weekly',
  'monthly',
  'weekend',
  'weekday',
  'days',
] as const
export type RecursPeriod = (typeof RECURS_PERIODS)[number]

export const recursPeriodValidator = v.union(
  v.literal('daily'),
  v.literal('everyOtherDay'),
  v.literal('weekly'),
  v.literal('monthly'),
  v.literal('weekend'),
  v.literal('weekday'),
  v.literal('days'),
)

export const recursDaysValidator = v.array(v.number())

// Periods tracked per day (one check-in per scheduled day).
export const PER_DAY_PERIODS: ReadonlySet<string> = new Set([
  'daily',
  'everyOtherDay',
  'weekday',
  'weekend',
  'days',
])

export const WEEKDAYS = [1, 2, 3, 4, 5]
export const WEEKEND = [0, 6]
// Monday-first display order.
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]

export type Schedule = {
  recursPeriod?: string | null
  recurrenceDay?: number | null
  recursDays?: readonly number[] | null
  oneOff?: boolean | null
}

/** Dedupe, sort and range-check (0–6). Undefined when nothing valid remains. */
export function normalizeRecursDays(days: readonly unknown[] | null | undefined) {
  if (!Array.isArray(days)) return undefined
  const out = [
    ...new Set(days.filter((d): d is number => Number.isInteger(d) && d >= 0 && d <= 6)),
  ].sort((a, b) => a - b)
  return out.length ? out : undefined
}

const sameSet = (a: number[], b: number[]) => a.length === b.length && a.every((d) => b.includes(d))

/**
 * Canonical {recursPeriod, recursDays} for a set of days: all 7 → daily, Mon–Fri
 * → weekday, Sat+Sun → weekend, one day → weekly on that day, otherwise 'days'.
 * Empty/invalid falls back to `fallback` (default daily).
 */
export function scheduleFromDays(
  days: readonly unknown[] | null | undefined,
  fallback: Exclude<RecursPeriod, 'days'> = 'daily',
): { recursPeriod: RecursPeriod; recursDays?: number[]; recurrenceDay?: number } {
  const d = normalizeRecursDays(days)
  if (!d) return { recursPeriod: fallback }
  if (d.length === 7) return { recursPeriod: 'daily' }
  if (sameSet(d, WEEKDAYS)) return { recursPeriod: 'weekday' }
  if (sameSet(d, WEEKEND)) return { recursPeriod: 'weekend' }
  if (d.length === 1) return { recursPeriod: 'weekly', recurrenceDay: d[0] }
  return { recursPeriod: 'days', recursDays: d }
}

const dowOf = (dateKey: string) => new Date(`${dateKey}T12:00:00Z`).getUTCDay()

/** Whether a recurrence with these fields is scheduled on `dateKey` (YYYY-MM-DD). */
export function dueOn(
  period: string | null | undefined,
  recurrenceDay: number | null | undefined,
  dateKey: string,
  recursDays?: readonly number[] | null,
): boolean {
  const dow = dowOf(dateKey)
  switch (period ?? 'daily') {
    case 'weekend':
      return dow === 0 || dow === 6
    case 'weekday':
      return dow >= 1 && dow <= 5
    case 'weekly':
      return dow === (recurrenceDay ?? 1)
    case 'monthly':
      return new Date(`${dateKey}T12:00:00Z`).getUTCDate() === (recurrenceDay ?? 1)
    case 'days':
      // An empty/missing set would make the habit invisible forever; treat as daily.
      return recursDays?.length ? recursDays.includes(dow) : true
    default:
      return true // daily / everyOtherDay (cycle isn't tracked; treat as daily)
  }
}

/** Task-shaped convenience over `dueOn`. */
export function isDueOn(task: Schedule, dateKey: string): boolean {
  if (task.oneOff) return false
  return dueOn(task.recursPeriod, task.recurrenceDay, dateKey, task.recursDays)
}

/** Count of scheduled days among `keys`, for completion-rate denominators. */
export function expectedDueCount(task: Schedule, keys: readonly string[]): number {
  const n = keys.filter((k) => isDueOn(task, k)).length
  return task.recursPeriod === 'everyOtherDay' ? Math.ceil(n / 2) : n
}

/**
 * Validated schedule fields for a requested period. 'days' is canonicalised via
 * `scheduleFromDays` (invalid/empty days → daily); other periods ignore `days`.
 */
export function resolveSchedule(
  period: RecursPeriod | null | undefined,
  days?: readonly unknown[] | null,
): { recursPeriod: RecursPeriod; recursDays?: number[]; recurrenceDay?: number } {
  if (period === 'days') return scheduleFromDays(days)
  return { recursPeriod: period ?? 'daily' }
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** English cadence text for AI prompts / MCP, e.g. "Mon/Wed/Fri" for 'days'. */
export function scheduleText(
  period: string | null | undefined,
  recursDays?: readonly number[] | null,
) {
  if (period === 'days' && recursDays?.length)
    return WEEK_ORDER.filter((d) => recursDays.includes(d))
      .map((d) => DAY_ABBR[d])
      .join('/')
  return period ?? 'daily'
}

/** Localised weekday names indexed by getDay() (0 = Sun). */
export function weekdayNames(locale: string, width: 'narrow' | 'short' = 'short'): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: width, timeZone: 'UTC' })
  // 2026-10-18 is a Sunday.
  return Array.from({ length: 7 }, (_, d) => fmt.format(Date.UTC(2026, 9, 18 + d)))
}

/** "Mon · Wed · Fri" (Monday-first, localised) for a 'days' schedule. */
export function recursDaysLabel(days: readonly number[], locale: string): string {
  const names = weekdayNames(locale)
  return WEEK_ORDER.filter((d) => days.includes(d))
    .map((d) => names[d])
    .join(' · ')
}

/** The days of week a schedule falls on, for seeding a day picker. */
export function scheduleDays(
  period: string | null | undefined,
  recurrenceDay?: number | null,
  recursDays?: readonly number[] | null,
): number[] {
  switch (period ?? 'daily') {
    case 'days':
      return recursDays?.length ? [...recursDays] : [0, 1, 2, 3, 4, 5, 6]
    case 'weekday':
      return [...WEEKDAYS]
    case 'weekend':
      return [...WEEKEND]
    case 'weekly':
      return [recurrenceDay ?? 1]
    default:
      return [0, 1, 2, 3, 4, 5, 6]
  }
}
